import apiClient from '../client';
import { InvoiceDto, InvoiceGenerationDto, InvoiceUpdateRequestDto } from 'interfaces/orders/invoice';
import { OrderBranchNoCompanyError, OrderStatusNotEligibleError } from './invoiceErrors';
import { FISCAL_ROUND_TRIP_TIMEOUT_MS, invoicingRequest } from '../invoicingErrors';

export { OrderStatusNotEligibleError, OrderBranchNoCompanyError } from './invoiceErrors';

const withKnownInvoiceErrors = async <T>(call: () => Promise<T>): Promise<T> => {
  try {
    return await call();
  } catch (error: any) {
    if (error?.code === 'ORDER_STATUS_NOT_ELIGIBLE') {
      throw new OrderStatusNotEligibleError();
    }
    if (error?.code === 'ORDER_BRANCH_NO_COMPANY') {
      throw new OrderBranchNoCompanyError();
    }
    throw error;
  }
};

interface InvoiceService {
  list(orderId: string): Promise<InvoiceDto[]>;
  generate(orderId: string): Promise<InvoiceDto>;
  generation(orderId: string): Promise<InvoiceGenerationDto>;
  update(orderId: string, invoiceId: string, data: InvoiceUpdateRequestDto): Promise<InvoiceDto>;
  get(orderId: string, invoiceId: string): Promise<InvoiceDto>;
  issue(orderId: string, invoiceId: string): Promise<InvoiceDto>;
  cancel(orderId: string, invoiceId: string): Promise<InvoiceDto>;
}

const endpoints = {
  list: (orderId: string) => `/orders/${orderId}/invoices`,
  generate: (orderId: string) => `/orders/${orderId}/invoices`,
  generation: (orderId: string) => `/orders/${orderId}/invoices/generation`,
  get: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}`,
  issue: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}/issue`,
  cancel: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}/cancel`,
};

/** Issue and cancel may look the document up at AADE or the provider and then send to it. */
const postWaitingOnAade = <T>(url: string) =>
  withKnownInvoiceErrors(() => invoicingRequest<T>(() => apiClient.post(url, undefined, { timeout: FISCAL_ROUND_TRIP_TIMEOUT_MS })));

/**
 * Every rejection surfaces as an `InvoicingApiError` (code + details), except the two order-level
 * codes that have their own error classes.
 */
export const invoicesService: InvoiceService = {
  list: orderId => invoicingRequest(() => apiClient.get(endpoints.list(orderId))),
  generate: orderId => withKnownInvoiceErrors(() => invoicingRequest(() => apiClient.post(endpoints.generate(orderId)))),
  update: (orderId, invoiceId, data) =>
    withKnownInvoiceErrors(() => invoicingRequest(() => apiClient.patch(endpoints.get(orderId, invoiceId), data))),
  get: (orderId, invoiceId) => invoicingRequest(() => apiClient.get(endpoints.get(orderId, invoiceId))),
  generation: orderId => invoicingRequest(() => apiClient.get(endpoints.generation(orderId))),
  // A 200 is not an issuance: the returned status (ISSUED, FAILED, PENDING) is the outcome.
  issue: (orderId, invoiceId) => postWaitingOnAade(endpoints.issue(orderId, invoiceId)),
  cancel: (orderId, invoiceId) => postWaitingOnAade(endpoints.cancel(orderId, invoiceId)),
};
